import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent } from 'react';
import { type ProductSortEnum, type ProductViewEnum } from '../../../__generated__/graphql-client-types';
import { booleanFromQueryString } from '../../../helpers/general-helper/general-helper';
import { isRangeFacetGroup } from '../../../helpers/search-helper/search-helper';
import { useProductViewPreference, useSearchResults } from '../../../hooks/apollo/search/search.hooks';
import { StyledButton } from '../../buttons';
import { EnhancedSelectBox } from '../../inputs/select-box/enhanced-select-box.component';
import { type SelectOptionProps } from '../../inputs/select-box/select-box.component';
import FilterAltIcon from '../../svgs/filter-alt.svg';
import FormatListBulletedIcon from '../../svgs/format-list-bulleted.svg';
import GridViewIcon from '../../svgs/grid-view.svg';
import SortIcon from '../../svgs/sort.svg';

/*
 * These updated constants for select box options have been intentionally kept local to this
 * component as it is part of an A/B test of a new search page header. If we decide to
 * productionalize the new header, we will move them into a constants file at that point.
 */

const SEARCH_SORT_OPTIONS: SelectOptionProps[] = [
	{ name: 'Best Match', value: 'BEST_MATCH' },
	{ name: 'Customer Rating', value: 'RATING' },
	{ name: 'Price: Low to High', value: 'PRICE_LOW_HI' },
	{ name: 'Price: High to Low', value: 'PRICE_HI_LOW' },
	{ name: 'Model Number', value: 'MODEL_NUMBER' },
	{ name: 'Newest', value: 'NEWEST' }
];

const RESULT_VIEW_TYPE_OPTIONS: SelectOptionProps[] = [
	{ name: 'Grid', value: 'TILE' },
	{ name: 'List', value: 'LIST' }
];

const STOCK_STATUS_OPTIONS: SelectOptionProps[] = [
	{ name: 'Hide', value: 'false' },
	{ name: 'Show', value: 'true' }
];

export type SearchHeaderControlsProps = {
	showNonstock?: boolean;
	onShowDrawer: () => void;
	className?: string;
};

export const SearchHeaderControls: FunctionComponent<SearchHeaderControlsProps> = ({ onShowDrawer, showNonstock, className = '' }) => {
	const { results, previousResults, loading, sortBy, setSortBy, isNonstock, setNonstock } = useSearchResults();
	const { productViewPreference = 'TILE', setProductViewPreference } = useProductViewPreference();
	const productSearchResult = previousResults?.products?.length ? previousResults : null;
	const searchResults = loading ? productSearchResult : results;

	const selectedFacetCounts =
		searchResults?.selectedFacetGroups?.reduce((facetCount, group) => {
			const isRangeGroup = isRangeFacetGroup(group);
			return facetCount + (isRangeGroup ? 1 : group.facets?.length ?? 0);
		}, 0) ?? 0;

	const viewChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const view = event.target.value as ProductViewEnum;
		setProductViewPreference(view).catch(logError);
	};
	const sortChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const sort = event.target.value as ProductSortEnum;
		setSortBy(sort);
	};
	const stockStatusChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const nonstock = booleanFromQueryString(event.target.value);
		setNonstock(nonstock);
	};

	const facetCountText = selectedFacetCounts > 0 ? `(${selectedFacetCounts})` : '';
	const viewTypeIcon =
		productViewPreference === 'LIST' ? <FormatListBulletedIcon data-testid="FormatListBulletedIcon" /> : <GridViewIcon />;

	return (
		<div className={`${className} flex flex-wrap items-end ga2`}>
			{showNonstock && (
				<EnhancedSelectBox
					name="stockStatusSelect"
					value={String(isNonstock)}
					options={STOCK_STATUS_OPTIONS}
					automationHook="product-stock-status"
					label="Nonstock"
					id="stock-status-select"
					ariaLabel="stock status"
					onChange={stockStatusChanged}
					data-testid="stock-status-select"
					employee={true}
					className="flex-basis-0 flex-grow-1 flex-none-l"
					selectClassName="w-100"
				/>
			)}
			<EnhancedSelectBox
				name="sortSelect"
				label="Sort"
				id="sort-select"
				value={sortBy}
				options={SEARCH_SORT_OPTIONS}
				automationHook="product-sort-by"
				ariaLabel="sort"
				onChange={sortChanged}
				data-testid="sort-select"
				borderStyle="dark"
				className="flex-basis-0 flex-grow-1 flex-none-l"
				selectClassName="w-100"
				icon={<SortIcon className="f4" />}
			/>
			<EnhancedSelectBox
				name="resultViewType"
				options={RESULT_VIEW_TYPE_OPTIONS}
				value={productViewPreference}
				label="View"
				id="result-view-type-select"
				ariaLabel="Select result view"
				borderStyle="dark"
				icon={viewTypeIcon}
				onChange={viewChanged}
				className="flex-basis-0 flex-grow-1 flex-none-l"
				selectClassName="w-100"
			/>
			<div className="dn-l flex-basis-0 flex-grow-1 minw-minc">
				<div className="w-100">
					<StyledButton onClick={onShowDrawer} buttonStyle="SECONDARY">
						<span className="flex items-center justify-center pr2 h1 ba b--transparent">
							<FilterAltIcon className="f4 pr1 flex-shrink-0" /> <span className="nowrap">Filter {facetCountText}</span>
						</span>
					</StyledButton>
				</div>
			</div>
		</div>
	);
};
